import { useSuspenseQuery } from "@apollo/client";
import {
  BreadcrumbArray,
  DataTable,
  DataTableColumnHeader,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/currencycloud_/$accountId_/balances/$balanceId",
)({
  head: () => opsHead("Currencycloud Balance Transactions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { accountId, balanceId } = useParams({ strict: false });
  if (!accountId || !balanceId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminCurrencycloudBalanceTransactions(
        $accountId: ID!
        $balanceId: ID!
      ) {
        currencycloudAccount(id: $accountId) {
          id
          accountName
          balance(id: $balanceId) {
            id
            currency
            amount
            transactions {
              id
              balanceId
              accountId
              currency
              amount
              balanceAmount
              type
              action
              status
              reason
              relatedEntityType
              relatedEntityId
              relatedEntityShortReference
              settlesAt
              completedAt
              createdAt
              updatedAt
            }
          }
        }
      }
    `),
    { variables: { accountId, balanceId } },
  );

  const account = data.currencycloudAccount;
  const balance = account?.balance;
  if (!account || !balance) {
    throw new NotFoundError();
  }

  return (
    <AdminPageContainer fullWidth>
      <AdminPageHeader title={`${balance.currency} balance transactions`} />
      <BreadcrumbArray
        stack={[
          { title: "Currencycloud", to: "/currencycloud" },
          {
            title: account.accountName,
            to: `/currencycloud/${account.id}`,
          },
          { title: balance.currency },
        ]}
      />

      <DataTable
        data={balance.transactions}
        className="mt-8 overflow-x-auto whitespace-nowrap border"
        hidePagination={true}
        pageSize={10_000}
        columns={[
          {
            accessorKey: "createdAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="created_at" />
            ),
            cell: ({ row }) => row.original.createdAt.toISOString(),
          },
          {
            accessorKey: "completedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="completed_at" />
            ),
            cell: ({ row }) => row.original.completedAt?.toISOString(),
          },
          {
            accessorKey: "settlesAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="settles_at" />
            ),
            cell: ({ row }) => row.original.settlesAt?.toISOString(),
          },
          {
            accessorKey: "updatedAt",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="updated_at" />
            ),
            cell: ({ row }) => row.original.updatedAt?.toISOString(),
          },
          {
            accessorKey: "type",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="type" />
            ),
          },
          {
            accessorKey: "action",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="action" />
            ),
          },
          {
            accessorKey: "status",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="status" />
            ),
          },
          {
            accessorKey: "amount",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="amount" />
            ),
            cell: ({ row }) => (
              <span className="font-mono">
                {new Decimal(row.original.amount).toFixed()}{" "}
                {row.original.currency}
              </span>
            ),
          },
          {
            accessorKey: "balanceAmount",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="balance_amount" />
            ),
            cell: ({ row }) => (
              <span className="font-mono">
                {row.original.balanceAmount === null ||
                row.original.balanceAmount === undefined
                  ? "null"
                  : new Decimal(row.original.balanceAmount).toFixed()}
              </span>
            ),
          },
          {
            accessorKey: "relatedEntityType",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="related_entity_type"
              />
            ),
            cell: ({ row }) => row.original.relatedEntityType ?? "null",
          },
          {
            accessorKey: "relatedEntityShortReference",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="related_entity_short_reference"
              />
            ),
            cell: ({ row }) => (
              <span className="font-mono">
                {row.original.relatedEntityShortReference}
              </span>
            ),
          },
          {
            accessorKey: "relatedEntityId",
            header: ({ column }) => (
              <DataTableColumnHeader
                column={column}
                title="related_entity_id"
              />
            ),
            cell: ({ row }) => (
              <span className="font-mono">
                {row.original.relatedEntityId ?? "null"}
              </span>
            ),
          },
          {
            accessorKey: "reason",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="reason" />
            ),
            cell: ({ row }) => row.original.reason || "null",
          },
          {
            accessorKey: "id",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="id" />
            ),
            cell: ({ row }) => (
              <span className="font-mono">{row.original.id}</span>
            ),
          },
        ]}
      />
    </AdminPageContainer>
  );
}
